import type { PointerEvent as ReactPointerEvent } from "react";
import type { Camera } from "@/renderer/camera";
import type { ViewportScreenInsets } from "@/renderer/renderHelpers";
import { formatRulerLabel, RULER_SIZE, rulerTicksForRange } from "@/editor/tools/rulerTicks";

interface RulerOverlayProps {
  camera: Camera;
  visibleViewportInsets: ViewportScreenInsets;
  canvasWidth: number;
  canvasHeight: number;
  pointerCanvasOffset?: { x: number; y: number };
  pointerDocumentPosition?: { x: number; y: number };
  pointerInsideCanvas: boolean;
  onHorizontalRulerPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onVerticalRulerPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
}

export function RulerOverlay({
  camera,
  visibleViewportInsets,
  canvasWidth,
  canvasHeight,
  pointerCanvasOffset,
  pointerDocumentPosition,
  pointerInsideCanvas,
  onHorizontalRulerPointerDown,
  onVerticalRulerPointerDown,
}: RulerOverlayProps) {
  const horizontalLeft = visibleViewportInsets.left + RULER_SIZE;
  const horizontalTop = visibleViewportInsets.top;
  const horizontalWidth = Math.max(0, canvasWidth - visibleViewportInsets.left - visibleViewportInsets.right - RULER_SIZE);
  const verticalLeft = visibleViewportInsets.left;
  const verticalTop = visibleViewportInsets.top + RULER_SIZE;
  const verticalHeight = Math.max(0, canvasHeight - visibleViewportInsets.top - visibleViewportInsets.bottom - RULER_SIZE);

  const visibleDocumentWidth = horizontalWidth / Math.max(0.0001, camera.zoom);
  const visibleDocumentHeight = verticalHeight / Math.max(0.0001, camera.zoom);
  const documentOriginX = camera.x + horizontalLeft / Math.max(0.0001, camera.zoom);
  const documentOriginY = camera.y + verticalTop / Math.max(0.0001, camera.zoom);
  const horizontalTicks = rulerTicksForRange(camera.zoom, documentOriginX, documentOriginX + visibleDocumentWidth);
  const verticalTicks = rulerTicksForRange(camera.zoom, documentOriginY, documentOriginY + visibleDocumentHeight);

  const pointerScreenX = pointerCanvasOffset ? pointerCanvasOffset.x - horizontalLeft : undefined;
  const pointerScreenY = pointerCanvasOffset ? pointerCanvasOffset.y - verticalTop : undefined;

  return (
    <>
      <div
        className="pointer-events-auto absolute z-30 bg-[#1a1d22] shadow-[inset_0_-1px_0_#ffffff1a]"
        style={{
          left: visibleViewportInsets.left,
          top: visibleViewportInsets.top,
          width: RULER_SIZE,
          height: RULER_SIZE,
        }}
      />
      <div
        className="pointer-events-auto absolute z-30 overflow-hidden bg-[#1a1d22] shadow-[inset_0_-1px_0_#ffffff1a]"
        style={{
          left: horizontalLeft,
          top: horizontalTop,
          width: horizontalWidth,
          height: RULER_SIZE,
        }}
        onPointerDown={onHorizontalRulerPointerDown}
      >
        <svg className="h-full w-full" aria-hidden="true">
          {horizontalTicks.map(tick => {
            const x = (tick.value - documentOriginX) * camera.zoom;
            if (x < -1 || x > horizontalWidth + 1) {
              return null;
            }

            return (
              <g key={`h-${tick.value}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={RULER_SIZE}
                  y2={RULER_SIZE - tick.lengthPx}
                  stroke="#94a3b8"
                  strokeWidth={1}
                />
                {tick.kind === "major" ? (
                  <text
                    x={x + 2}
                    y={11}
                    fill="#cbd5e1"
                    fontSize={10}
                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                  >
                    {formatRulerLabel(tick.value)}
                  </text>
                ) : null}
              </g>
            );
          })}
          {pointerInsideCanvas && pointerScreenX !== undefined ? (
            <>
              <line x1={pointerScreenX} x2={pointerScreenX} y1={0} y2={RULER_SIZE} stroke="#60a5fa" strokeWidth={1} />
              <rect x={pointerScreenX + 6} y={2} width={54} height={16} rx={3} fill="#0f172a" stroke="#334155" />
              <text x={pointerScreenX + 10} y={13} fill="#e2e8f0" fontSize={10} fontFamily="ui-sans-serif, system-ui, sans-serif">
                X: {formatRulerLabel(pointerDocumentPosition!.x)}
              </text>
            </>
          ) : null}
        </svg>
      </div>
      <div
        className="pointer-events-auto absolute z-30 overflow-hidden bg-[#1a1d22] shadow-[inset_-1px_0_0_#ffffff1a]"
        style={{
          left: verticalLeft,
          top: verticalTop,
          width: RULER_SIZE,
          height: verticalHeight,
        }}
        onPointerDown={onVerticalRulerPointerDown}
      >
        <svg className="h-full w-full" aria-hidden="true">
          {verticalTicks.map(tick => {
            const y = (tick.value - documentOriginY) * camera.zoom;
            if (y < -1 || y > verticalHeight + 1) {
              return null;
            }

            return (
              <g key={`v-${tick.value}`}>
                <line
                  x1={RULER_SIZE}
                  x2={RULER_SIZE - tick.lengthPx}
                  y1={y}
                  y2={y}
                  stroke="#94a3b8"
                  strokeWidth={1}
                />
                {tick.kind === "major" ? (
                  <text
                    x={RULER_SIZE - 2}
                    y={y}
                    textAnchor="start"
                    dominantBaseline="middle"
                    fill="#cbd5e1"
                    fontSize={10}
                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                    transform={`rotate(-90 ${RULER_SIZE - 12} ${y})`}
                  >
                    {formatRulerLabel(tick.value)}
                  </text>
                ) : null}
              </g>
            );
          })}
          {pointerInsideCanvas && pointerScreenY !== undefined ? (
            <>
              <line x1={0} x2={RULER_SIZE} y1={pointerScreenY} y2={pointerScreenY} stroke="#60a5fa" strokeWidth={1} />
              <rect x={RULER_SIZE - 14} y={pointerScreenY + 6} width={16} height={54} rx={3} fill="#0f172a" stroke="#334155" />
              <text
                x={RULER_SIZE - 44}
                y={pointerScreenY + 14}
                textAnchor="start"
                dominantBaseline="middle"
                fill="#e2e8f0"
                fontSize={10}
                fontFamily="ui-sans-serif, system-ui, sans-serif"
                transform={`rotate(-90 ${RULER_SIZE - 4} ${pointerScreenY + 14})`}
              >
                Y: {formatRulerLabel(pointerDocumentPosition!.y)}
              </text>
            </>
          ) : null}
        </svg>
      </div>
    </>
  );
}

export { RULER_SIZE };
